/**
 * @module Model.CoachProfileModel
 *
 */
import { Model } from "typedefs";
import { LessonRateModel } from "./LessonRateModel";
import { DateTime } from "luxon";

export interface CoachProfileResponse extends Model {
  id: string;
  /**
   * Whether this coach takes public bookings. The public coach endpoint is
   * scoped `where(calendar_uuid:, public_calendar: true)`, so a coach with it
   * off cannot be opened from a public page at all.
   */
  publicCalendar?: boolean;
  email: string;
  firstName: string;
  lastName: string;
  timeZone: string;
  externalId: string;
  facilityExternalId: string;
  calendarUuid: string;
  orderNumber: number;
  currencySymbol: string;
  currency: string;
  profilePicture: string | null;
  title?: string | null;
  earliestBookingDate?: string;
  latestBookingDate?: string;
  allowsPayments?: boolean;
  stripeIntegrationEnabled?: boolean;
  /**
   * CFTB-3431: the coach's own calendar settings, carried by the public payload so
   * their public calendar can start the week and write the clock the way they chose.
   * Absent on the authenticated payloads, where the reader's own preferences apply.
   */
  calendarFirstDayOfWeek?: number;
  calendarTimeFormat?: "12h" | "24h";
  calendarDateFormat?: "us" | "eu";
}

export class CoachProfileModel implements Model {
  id: string;
  email: string;
  lessonRates: LessonRateModel[];
  timeZone: string;
  externalId: string;
  facilityExternalId: string;
  facilityId: string;
  orderNumber: number;
  calendarSubscriptionUrl: string;
  currencySymbol: string;
  currency: string;
  firstName: string;
  lastName: string;
  profilePicture: string | null;
  /** The coach's title at this facility, e.g. "PGA Master", set in the Users section. */
  title?: string | null;
  fullName: string;
  earliestBookingDate?: DateTime;
  latestBookingDate?: DateTime;
  calendarUuid: string;
  /** See `CoachProfileResponse` — false means they cannot be booked publicly. */
  publicCalendar?: boolean;
  allowScheduleProviderAccess: boolean;
  allowsPayments?: boolean;
  stripeIntegrationEnabled?: boolean;
  /** See `CoachProfileResponse` — the coach's calendar settings, public payload only. */
  calendarFirstDayOfWeek?: number;
  calendarTimeFormat?: "12h" | "24h";
  calendarDateFormat?: "us" | "eu";

  constructor(coachProfile: CoachProfileResponse) {
    this.id = coachProfile.id;
    this.firstName = coachProfile.firstName;
    this.lastName = coachProfile.lastName;
    this.email = coachProfile.email;
    this.timeZone = coachProfile.timeZone;
    this.externalId = coachProfile.externalId;
    this.facilityExternalId = coachProfile.facilityExternalId;
    this.facilityId = coachProfile.facilityId;
    this.orderNumber = coachProfile.orderNumber;
    this.calendarSubscriptionUrl = coachProfile.calendarSubscriptionUrl;
    this.currencySymbol = coachProfile.currencySymbol;
    this.currency = coachProfile.currency;
    this.profilePicture = coachProfile.profilePicture;
    this.publicCalendar = coachProfile.publicCalendar;
    this.title = coachProfile.title;
    this.fullName = `${this.firstName} ${this.lastName}`;
    this.calendarUuid = coachProfile.calendarUuid;
    this.allowScheduleProviderAccess = coachProfile.allowScheduleProviderAccess;
    this.allowsPayments = coachProfile.allowsPayments;
    this.stripeIntegrationEnabled = coachProfile.stripeIntegrationEnabled;
    this.calendarFirstDayOfWeek = coachProfile.calendarFirstDayOfWeek;
    this.calendarTimeFormat = coachProfile.calendarTimeFormat;
    this.calendarDateFormat = coachProfile.calendarDateFormat;

    this.lessonRates = [];
    if (coachProfile.lessonRates && coachProfile.lessonRates.length > 0) {
      this.lessonRates = coachProfile.lessonRates.map(
        (lessonRate: any) => new LessonRateModel(lessonRate)
      );
    }

    if (coachProfile.earliestBookingDate) {
      this.earliestBookingDate = DateTime.fromISO(coachProfile.earliestBookingDate, { zone: this.timeZone });
    }

    if (coachProfile.latestBookingDate) {
      this.latestBookingDate = DateTime.fromISO(coachProfile.latestBookingDate, { zone: this.timeZone });
    }
  }

  get defaultLessonRate(): LessonRateModel | null {
    if (!this.lessonRates || this.lessonRates.length === 0) {
      return null;
    }

    return this.lessonRates.find(lr => lr.defaultRate) || this.lessonRates[0];
  }
}
